import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Gift, Heart, Sparkles, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const SHOPIFY_STORE = 'https://gifts.charmingcreationsgifts.com';

const GiftCardsPage = () => {
  const giftCardOptions = [
    { value: 25, popular: false },
    { value: 50, popular: true },
    { value: 75, popular: false },
    { value: 100, popular: false },
    { value: 150, popular: false },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-pink-50">
      <Helmet>
        <title>Gift Cards | Charming Creations Australia</title>
        <meta name="description" content="Give the gift of choice with a Charming Creations gift card. Perfect for any occasion. Let them choose their favourite handcrafted resin art jewellery or homewares." />
        <link rel="canonical" href="https://www.charmingcreationsgifts.com/gift-cards" />
      </Helmet>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-200 shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                alt="Charming Creations Logo" 
                className="w-12 h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-teal-600 bg-clip-text text-transparent">
                  Charming Creations
                </h1>
              </div>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-amber-300 hover:bg-amber-50">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-pink-500 to-rose-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <Gift className="w-20 h-20 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Gift Cards</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto">
            Give the gift of choice! Let them pick their perfect handcrafted treasure
          </p>
        </div>
      </section>

      {/* Gift Card Options */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose an Amount</h2>
            <p className="text-gray-600">
              Our digital gift cards are delivered instantly via email and never expire
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
            {giftCardOptions.map((option, idx) => (
              <a
                key={idx}
                href={`${SHOPIFY_STORE}/products/gift-card?variant=${option.value}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Card className={`relative border-2 transition-all hover:shadow-lg hover:scale-105 ${
                  option.popular ? 'border-pink-400 bg-pink-50' : 'border-gray-200 hover:border-pink-300'
                }`}>
                  {option.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-pink-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                        Popular
                      </span>
                    </div>
                  )}
                  <CardContent className="p-6 text-center">
                    <p className="text-3xl font-bold text-gray-900">${option.value}</p>
                    <p className="text-sm text-gray-500 mt-1">AUD</p>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>

          {/* Custom Amount */}
          <div className="text-center mb-16">
            <p className="text-gray-600 mb-4">Need a different amount?</p>
            <a
              href={`${SHOPIFY_STORE}/products/gift-card`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="border-2 border-pink-400 text-pink-600 hover:bg-pink-50">
                Choose Custom Amount
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">How Gift Cards Work</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-pink-500">1</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Choose Amount</h3>
                <p className="text-gray-600 text-sm">
                  Select from our preset amounts or enter a custom value
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-pink-500">2</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Send Instantly</h3>
                <p className="text-gray-600 text-sm">
                  Digital gift card delivered straight to their email inbox
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-pink-500">3</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">They Shop!</h3>
                <p className="text-gray-600 text-sm">
                  Recipient chooses their favourite pieces from our collection
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Card className="border-2 border-amber-200">
              <CardContent className="p-6 text-center">
                <Sparkles className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">Never Expires</h3>
                <p className="text-gray-600 text-sm">
                  No rush - gift cards are valid forever
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-teal-200">
              <CardContent className="p-6 text-center">
                <Gift className="w-10 h-10 text-teal-500 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">Instant Delivery</h3>
                <p className="text-gray-600 text-sm">
                  Sent directly via email in seconds
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-pink-200">
              <CardContent className="p-6 text-center">
                <Heart className="w-10 h-10 text-pink-500 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">Personal Message</h3>
                <p className="text-gray-600 text-sm">
                  Add a heartfelt note at checkout
                </p>
              </CardContent>
            </Card>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Not sure what they'd like?
            </h3>
            <p className="text-gray-600 mb-6">
              A gift card lets them choose exactly what they love!
            </p>
            <a
              href={`${SHOPIFY_STORE}/products/gift-card`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white px-8 py-6 text-lg">
                <Gift className="w-5 h-5 mr-2" />
                Buy Gift Card
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GiftCardsPage;
